package io.horion.cv.pages.dashboard

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.breakpoint.rememberBreakpoint
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import io.horion.cv.components.auth.RequireAuth
import io.horion.cv.components.preview.LetterPreviewPanel
import io.horion.cv.components.widgets.AlertMessage
import io.horion.cv.components.widgets.ToastType
import io.horion.cv.di.koinGet
import io.horion.cv.models.reaccordionSections
import io.horion.cv.pages.cv.Separator
import io.horion.cv.pages.motivation.ReAccordionRow
import io.horion.cv.pages.motivation.TelechargerButton
import io.horion.cv.pages.navigation.Screen
import io.horion.cv.styles.ReAccordionSectionPaddingStyle
import io.horion.cv.utils.Colors
import io.horion.cv.viewModel.LetterViewModel
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Style
import org.jetbrains.compose.web.dom.Text

private sealed class LetterLoadState {
    data object Loading : LetterLoadState()
    data object Loaded : LetterLoadState()
    data object Error : LetterLoadState()
}

@OptIn(DelicateApi::class)
@Page("update-letter")
@Composable
fun UpdateLetterPage() = RequireAuth {

    val letterViewModel = koinGet<LetterViewModel>()
    val ctx = rememberPageContext()
    val breakpoint = rememberBreakpoint()
    val isMobile = breakpoint <= Breakpoint.MD
    val isTablet = breakpoint == Breakpoint.LG

    val letterId = ctx.route.queryParams["id"] ?: ""
    val letterModel = ctx.route.queryParams["model"]

    var loadState by remember { mutableStateOf<LetterLoadState>(LetterLoadState.Loading) }
    var showUpdateSuccess by remember { mutableStateOf(false) }
    var updateError by remember { mutableStateOf<String?>(null) }

    AlertMessage(
        message = "Lettre mise à jour avec succès",
        isVisible = showUpdateSuccess,
        type = ToastType.SUCCESS,
        duration = 1500,
        onDismiss = {
            showUpdateSuccess = false
            ctx.router.navigateTo(Screen.LetterDashboard.path)
        }
    )

    AlertMessage(
        message = updateError ?: "",
        isVisible = updateError != null,
        type = ToastType.ERROR,
        onDismiss = { updateError = null }
    )

    LaunchedEffect(letterViewModel.letterUiState.error) {
        letterViewModel.letterUiState.error?.let { updateError = it }
    }

    LaunchedEffect(letterId, letterModel) {
        letterViewModel.resetToBlank()

        loadState = if (letterId.isBlank()) {
            LetterLoadState.Error
        } else {
            val letter = letterViewModel.loadLetterForEditById(letterId, letterModel)
            if (letter != null) LetterLoadState.Loaded else LetterLoadState.Error
        }
    }

    val colorMode by ColorMode.currentState
    val isDark = colorMode.isDark

    Style {
        """
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes popUp {
            from { opacity: 0; transform: translateY(10px) scale(0.97); }
            to { opacity: 1; transform: translateY(0) scale(1); }
        }
        """.trimIndent()
    }

    Div(attrs = {
        style {
            property("display", "flex")
            property("flex-direction", "column")
            property("height", "100vh")
            property("width", "100%")
            property("overflow", "hidden")
            property("background-color", if (isDark) Colors.DarkBG.toString() else Colors.White.toString())
            property("transition", "background-color 300ms ease")
        }
    }) {
        when (loadState) {
            is LetterLoadState.Loading -> {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
                }) {
                    Text("Chargement de la lettre...")
                }
            }

            is LetterLoadState.Error -> {
                Div(attrs = {
                    style {
                        property("flex", "1")
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("gap", "16px")
                    }
                }) {
                    Text("Impossible de charger cette lettre.")
                    org.jetbrains.compose.web.dom.Button(attrs = {
                        onClick { ctx.router.navigateTo(Screen.LetterDashboard.path) }
                        style {
                            property("cursor", "pointer")
                            property("padding", "10px 20px")
                            property("border-radius", "8px")
                        }
                    }) {
                        Text("Retour au dashboard")
                    }
                }
            }

            is LetterLoadState.Loaded -> {
                if (isMobile) {
                    Div(attrs = {
                        style {
                            property("flex", "1")
                            property("overflow-y", "auto")
                            property("overflow-x", "hidden")
                            property("padding-bottom", "50px")
                        }
                    }) {
                        FormContentUpdateLetter(
                            isDark = isDark,
                            submitLabel = "Modifier",
                            onCustomSubmit = {
                                letterViewModel.updateExistingLetter {
                                    showUpdateSuccess = true
                                }
                            }
                        )
                    }
                } else {
                    Div(attrs = {
                        style {
                            property("display", "flex")
                            property("flex", "1")
                            property("min-height", "0")
                            property("overflow", "hidden")
                        }
                    }) {
                        Div(attrs = {
                            style {
                                property("flex", "1")
                                property("overflow-y", "auto")
                                property("overflow-x", "hidden")
                                property("padding-bottom", if (isTablet) "70px" else "100px")
                                property("background-color", if (isDark) Colors.DarkBG.toString() else Colors.White.toString())
                            }
                        }) {
                            FormContentUpdateLetter(
                                isDark = isDark,
                                submitLabel = "Modifier",
                                onCustomSubmit = {
                                    letterViewModel.updateExistingLetter {
                                        showUpdateSuccess = true
                                    }
                                }
                            )
                        }

                        Div(attrs = {
                            style {
                                property("width", "1px")
                                property("background-color", if (isDark) "#2a2a3e" else Colors.LightGray.toString())
                                property("flex-shrink", "0")
                            }
                        })

                        Div(attrs = {
                            style {
                                property("flex", "1")
                                property("overflow-y", "auto")
                                property("overflow-x", "hidden")
                                property("background-color", Colors.LightGray.toString())
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("align-items", "center")
                            }
                        }) {
                            LetterPreviewPanel(
                                letterUiState = letterViewModel.letterUiState,
                                model = letterViewModel.letterUiState.model,
                            )
                        }
                    }
                }
            }
        }
    }
}

@OptIn(DelicateApi::class)
@Composable
fun FormContentUpdateLetter(
    isDark: Boolean,
    submitLabel: String ,
    onCustomSubmit: (() -> Unit)? = null
) {
    var openedSectionId by remember { mutableStateOf<String?>(null) }

    Column(modifier = Modifier.fillMaxWidth()) {
        val allSections = reaccordionSections

        allSections.forEach { section ->
            val isOpen = section.id == openedSectionId

            ReAccordionRow(
                title = section.title,
                isOpen = isOpen,
                onClick = {
                    openedSectionId = if (isOpen) null else section.id
                },
                isDark = isDark
            )

            if (!isOpen) {
                Separator()
            }

            // Conteneur fluide occupant 100% de la largeur alignée
            Box(
                modifier = ReAccordionSectionPaddingStyle.toModifier()
                    .fillMaxWidth()
                    .styleModifier {
                        property("max-height", if (isOpen) "2000px" else "0px")
                        property("opacity", if (isOpen) "1" else "0")
                        property("transition", "max-height 500ms ease-in-out, opacity 300ms ease-in-out")
                    }
                    .overflow(Overflow.Hidden)
            ) {
                if (isOpen) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(topBottom = 25.px)
                    ) {
                        section.content()
                    }
                }
            }

            if (isOpen) {
                Separator()
            }
        }

        Box(modifier = Modifier.padding(5.px))

        TelechargerButton(
            label = submitLabel,
            onCustomSubmit = onCustomSubmit
        )
    }
}
